import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

const getComparisonPairRequestSchema = z.object({
  workplaceId: z.string(),
  shiftId: z.string().optional(),
});

const resourceIdentifierSchema = z.object({
  id: z.string(),
  type: z.string(),
});

const includedWorkplaceSchema = z.object({
  id: z.string(),
  type: z.literal("workplace"),
  attributes: z.object({
    name: z.string(),
    timeSlot: z.string().optional(),
    lastWorkedAt: z.string().optional(),
  }),
});

const getComparisonPairResponseSchema = z.object({
  data: z
    .object({
      id: z.string(),
      type: z.literal("workplace-comparison-pair"),
      relationships: z.object({
        workplaceA: z.object({ data: resourceIdentifierSchema }),
        workplaceB: z.object({ data: resourceIdentifierSchema }),
        shift: z.object({ data: resourceIdentifierSchema.nullable() }),
      }),
    })
    .nullable(),
  included: z.array(includedWorkplaceSchema).optional(),
});

export type GetComparisonPairRequest = z.infer<typeof getComparisonPairRequestSchema>;
export type GetComparisonPairResponse = z.infer<typeof getComparisonPairResponseSchema>;

const GET_COMPARISON_PAIR_PATH = "/workplace-comparisons/pair";

export function useGetComparisonPair(
  queryParams: GetComparisonPairRequest,
  options: UseGetQueryOptions<GetComparisonPairResponse> = {}
): UseQueryResult<GetComparisonPairResponse> {
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_SHIFT_REVIEW_API_URL}${GET_COMPARISON_PAIR_PATH}`,
    queryParams,
    requestSchema: getComparisonPairRequestSchema,
    responseSchema: getComparisonPairResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_COMPARISON_PAIR_FAILURE,
    },
    ...options,
  });
}
